export interface UniversityProgram {
  slug: string
  title: string
  shortTitle: string
  degreeLevel: string
  focusArea: string
  description: string
  websiteUrl: string
  jobOpportunitiesIntro: string
  jobOpportunities: string[]
  keyFacts: string[]
  curriculum: { course: string; credits: number }[]
}

export interface University {
  slug: string
  name: string
  location: string
  description: string
  accreditation: string
  phone: string
  email: string
  website: string
  programs: UniversityProgram[]
}

export const universities: University[] = [
  {
    slug: 'global-tech-college',
    name: 'Global Tech College',
    location: 'Toledo, OH',
    description:
      "Global Tech College offers career-focused degree and certificate programs designed to prepare students for success in today's competitive job market. With flexible online options and dedicated support, we help you achieve your educational goals.",
    accreditation:
      'Accrediting Commission of Career Schools and Colleges (ACCSC)',
    phone: '(419) 315-4388',
    email: 'info@globaltech.edu',
    website: 'https://globaltech.edu',
    programs: [
      {
        slug: 'computer-science-diploma',
        title: 'Computer Science Diploma',
        shortTitle: 'CS Diploma',
        degreeLevel: '1', // Certificate
        focusArea: '4', // Computers & IT
        websiteUrl: 'https://globaltech.edu/computer-science-diploma/',
        description:
          'The Computer Science Diploma program provides students with foundational skills in computer science, preparing them for entry-level positions in IT support, help desk, and computer technician roles.',
        jobOpportunitiesIntro:
          'Computer User Support Specialists including job titles such as',
        jobOpportunities: [
          'Computer Specialist',
          'Computer Support Specialist',
          'Computer Technician',
          'Desktop Support Technician',
          'Help Desk Analyst',
          'Help Desk Technician',
          'IT Support Specialist',
        ],
        keyFacts: [
          'Program Length: 1 year',
          'Diploma Program',
          'Title IV Approved Online Programs',
          'Federal Student Loans available for eligible students',
          'Federal PELL Grant available for eligible students',
        ],
        curriculum: [
          { course: 'Intro to Computers', credits: 4 },
          { course: 'Programming Fundamentals', credits: 4 },
          { course: 'Database Management', credits: 4 },
          { course: 'Network Fundamentals', credits: 4 },
          { course: 'Web Development', credits: 4 },
          { course: 'Cybersecurity Basics', credits: 4 },
        ],
      },
      {
        slug: 'associate-of-applied-science-computer-science',
        title: 'Computer Science Associate of Applied Science',
        shortTitle: 'AAS Computer Science',
        degreeLevel: '2', // Associates
        focusArea: '4', // Computers & IT
        websiteUrl:
          'https://globaltech.edu/associate-of-applied-science-computer-science/',
        description:
          'The Associate of Applied Science (AAS) – Computer Science is designed to provide students with the skills allowing them to be prepared for career growth and success in related fields in computer science. The program focuses on applying critical thinking in the process of decision making and problem solving in the Information Technology (IT) and Information Science (IS) fields.',
        jobOpportunitiesIntro:
          'Computer User Support Specialists including job titles such as',
        jobOpportunities: [
          'Computer Specialist',
          'Computer Support Specialist',
          'Computer Technician',
          'Desktop Support Technician',
          'Help Desk Analyst',
          'Help Desk Technician',
          'Information Technology Specialist (IT Specialist)',
          'Support Specialist',
          'Technical Support Specialist',
        ],
        keyFacts: [
          'Program Length: 2 years',
          'Total Credits Required: 96',
          'Title IV Approved Online Programs',
          'Federal Student Loans available for eligible students',
          'Federal PELL Grant available for eligible students',
        ],
        curriculum: [
          { course: 'Intro to Computers', credits: 4 },
          { course: 'Programming Fundamentals', credits: 4 },
          { course: 'Database Management', credits: 4 },
          { course: 'Network Fundamentals', credits: 4 },
          { course: 'Web Development', credits: 4 },
          { course: 'Operating Systems', credits: 4 },
          { course: 'Cybersecurity Basics', credits: 4 },
          { course: 'Systems Analysis', credits: 4 },
          { course: 'IT Project Management', credits: 4 },
          { course: 'Capstone Project', credits: 4 },
        ],
      },
      {
        slug: 'business-management-diploma',
        title: 'Business Management Diploma',
        shortTitle: 'Business Diploma',
        degreeLevel: '1', // Certificate
        focusArea: '1', // Business & Finance
        websiteUrl: 'https://globaltech.edu/business-management-diploma/',
        description:
          'The Business Management Diploma program prepares students with foundational business skills for entry-level positions in office management, customer service, and administrative support roles.',
        jobOpportunitiesIntro:
          'First-Line Supervisors of Office and Administrative Support Workers including potential job titles such as',
        jobOpportunities: [
          'Administrative Assistant',
          'Office Coordinator',
          'Customer Service Representative',
          'Office Manager',
          'Accounts Payable Clerk',
        ],
        keyFacts: [
          'Program Length: 1 year',
          'Diploma Program',
          'Title IV Approved Online Programs',
          'Federal Student Loans available for eligible students',
          'Federal PELL Grant available for eligible students',
        ],
        curriculum: [
          { course: 'Intro to Business', credits: 4 },
          { course: 'Principles of Management', credits: 4 },
          { course: 'Business Communications', credits: 4 },
          { course: 'Accounting I', credits: 4 },
          { course: 'Marketing Fundamentals', credits: 4 },
          { course: 'Business Law', credits: 4 },
        ],
      },
      {
        slug: 'associate-of-applied-business-business-management',
        title: 'Business Management Associate of Applied Business',
        shortTitle: 'AAB Business Management',
        degreeLevel: '2', // Associates
        focusArea: '1', // Business & Finance
        websiteUrl:
          'https://globaltech.edu/associate-of-applied-business-business-management/',
        description:
          'The Associate of Applied Business (AAB) – Business Management is designed to provide students with the skills to prepare them for career growth and success in business management-related fields. The program focuses on applying critical thinking in the process of decision making, problem-solving and leading, facilitating, and contributing to meeting organizational goals. This program is ideal for individuals looking to attain an entry-level or mid-level position in the field of business management.',
        jobOpportunitiesIntro:
          'First-Line Supervisors of Office and Administrative Support Workers including potential job titles such as',
        jobOpportunities: [
          'Accounting Manager',
          'Accounts Payable Supervisor',
          'Accounts Receivable Manager',
          'Administrative Supervisor',
          'Customer Service Manager',
          'Customer Service Supervisor',
          'Office Coordinator',
          'Office Manager',
          'Office Supervisor',
        ],
        keyFacts: [
          'Program Length: 2 years',
          'Total Credits Required: 96',
          'Title IV Approved Online Programs',
          'Federal Student Loans available for eligible students',
          'Federal PELL Grant available for eligible students',
        ],
        curriculum: [
          { course: 'Intro to Business', credits: 4 },
          { course: 'Principles of Management', credits: 4 },
          { course: 'Business Communications', credits: 4 },
          { course: 'Accounting I', credits: 4 },
          { course: 'Marketing Fundamentals', credits: 4 },
          { course: 'Human Resources Management', credits: 4 },
          { course: 'Business Law', credits: 4 },
          { course: 'Organizational Behavior', credits: 4 },
          { course: 'Strategic Management', credits: 4 },
          { course: 'Business Capstone', credits: 4 },
        ],
      },
    ],
  },
]

export function getUniversityBySlug(slug: string): University | undefined {
  return universities.find((u) => u.slug === slug)
}

export function getProgramBySlug(
  university: University,
  programSlug: string,
): UniversityProgram | undefined {
  return university.programs.find((p) => p.slug === programSlug)
}
